import { useState } from 'react';
import { Button, Input, Modal } from '@ab-inbev-maz/compass';
import { CANVAS_SIZES, CUSTOM_SIZE_ID, DEFAULT_CANVAS_SIZE } from './CanvasSizes';

export interface NewCanvasConfig {
  name: string;
  width: number;
  height: number;
  sizeId: string;
}

interface CreateCanvasModalProps {
  onClose: () => void;
  onCreate: (config: NewCanvasConfig) => void;
  // While the caller saves the canvas: the form is locked and closing is disabled.
  isSubmitting?: boolean;
  error?: string | null;
}

export const MIN_CANVAS_SIDE = 100;
export const MAX_CANVAS_SIDE = 5000;
const DEFAULT_CUSTOM_WIDTH = '1600';
const DEFAULT_CUSTOM_HEIGHT = '900';
const SHAPE_MAX_SIDE_PX = 58;

const cardClassName = (selected: boolean) =>
  `flex flex-col gap-3 p-3 border rounded-sm text-left cursor-pointer transition-colors ${
    selected ? 'border-[#FFCC00] bg-[#FAEFC0]' : 'border-gray-300 bg-white hover:border-gray-400'
  }`;

const inputClassName =
  'h-8 text-sm font-normal text-gray-600 [--form-field-padding-y:0.35rem] [--form-field-padding-x:0.6rem] [--form-field-default-border:1px_solid_#e5e7eb] [--form-field-default-bg:white]';

const parseSide = (value: string): number | null => {
  if (!/^\d+$/.test(value.trim())) return null;
  const side = Number.parseInt(value, 10);
  return side >= MIN_CANVAS_SIDE && side <= MAX_CANVAS_SIDE ? side : null;
};

// Initial setup for a new canvas: project name and size. Persisting it is left to the caller.
const CreateCanvasModal = ({ onClose, onCreate, isSubmitting = false, error }: CreateCanvasModalProps) => {
  const [name, setName] = useState('');
  const [sizeId, setSizeId] = useState(DEFAULT_CANVAS_SIZE.id);
  const [customWidth, setCustomWidth] = useState(DEFAULT_CUSTOM_WIDTH);
  const [customHeight, setCustomHeight] = useState(DEFAULT_CUSTOM_HEIGHT);

  const isCustom = sizeId === CUSTOM_SIZE_ID;
  const preset = CANVAS_SIZES.find((size) => size.id === sizeId);
  const width = isCustom ? parseSide(customWidth) : (preset?.width ?? null);
  const height = isCustom ? parseSide(customHeight) : (preset?.height ?? null);
  const trimmedName = name.trim();
  const canCreate = trimmedName.length > 0 && width !== null && height !== null;

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleCreate = () => {
    if (!canCreate || isSubmitting) return;
    onCreate({ name: trimmedName, width, height, sizeId });
  };

  return (
    <Modal open onClose={handleClose}>
      <div
        role="dialog"
        aria-label="Crear nuevo canvas"
        className="flex flex-col gap-6 p-8 bg-white rounded-xl shadow-lg w-full max-w-lg mx-auto"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-semibold text-gray-900">Crear nuevo canvas</h2>
            <p className="text-base text-gray-700">
              Ingresa el nombre y selecciona el tamaño para comenzar a diseñar tu canvas.
            </p>
          </div>
          <Button
            icon="validation/close"
            variant="secondary"
            aria-label="Cerrar"
            className="cursor-pointer [--button-border-width:0] [--button-secondary-default-bg:transparent]"
            disabled={isSubmitting}
            onClick={handleClose}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-canvas-name" className="text-xs font-semibold text-gray-700">
            Nombre del proyecto <span aria-hidden="true">*</span>
          </label>
          <Input
            id="new-canvas-name"
            name="new-canvas-name"
            placeholder="Ej. Promo cerveza junio"
            value={name}
            disabled={isSubmitting}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-gray-700">
            Tamaño del canva <span aria-hidden="true">*</span>
          </p>
          <div className="grid grid-cols-2 gap-4">
            {CANVAS_SIZES.map((size) => {
              const scale = SHAPE_MAX_SIDE_PX / Math.max(size.width, size.height);
              const selected = sizeId === size.id;
              return (
                <button
                  key={size.id}
                  type="button"
                  aria-pressed={selected}
                  disabled={isSubmitting}
                  onClick={() => setSizeId(size.id)}
                  className={cardClassName(selected)}
                >
                  <span className="flex items-center justify-center h-16">
                    <span
                      className={`border-2 bg-white rounded-sm ${selected ? 'border-[#FFCC00]' : 'border-gray-400'}`}
                      style={{ width: size.width * scale, height: size.height * scale }}
                    />
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-gray-900">{size.label}</span>
                    <span className="block text-xs text-gray-600">{size.dimensions}</span>
                  </span>
                </button>
              );
            })}

            <div
              role="button"
              tabIndex={0}
              aria-pressed={isCustom}
              aria-label="Personalizado"
              onClick={() => setSizeId(CUSTOM_SIZE_ID)}
              onKeyDown={(e) => {
                if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) setSizeId(CUSTOM_SIZE_ID);
              }}
              className={`${cardClassName(isCustom)} items-center justify-center`}
            >
              <div className="flex items-center gap-2" onFocus={() => setSizeId(CUSTOM_SIZE_ID)}>
                <Input
                  id="new-canvas-custom-width"
                  name="new-canvas-custom-width"
                  inputMode="numeric"
                  aria-label="Ancho personalizado"
                  value={customWidth}
                  disabled={isSubmitting}
                  onChange={(e) => setCustomWidth(e.target.value)}
                  className={inputClassName}
                />
                <span className="text-sm text-gray-500">x</span>
                <Input
                  id="new-canvas-custom-height"
                  name="new-canvas-custom-height"
                  inputMode="numeric"
                  aria-label="Alto personalizado"
                  value={customHeight}
                  disabled={isSubmitting}
                  onChange={(e) => setCustomHeight(e.target.value)}
                  className={inputClassName}
                />
              </div>
              <div className="text-center">
                <span className="block text-sm font-bold text-gray-900">Personalizado</span>
                <span className="block text-xs text-gray-600">Ancho x alto (px)</span>
                {isCustom && (width === null || height === null) && (
                  <span className="block text-xs text-red-600 mt-1">
                    Valores entre {MIN_CANVAS_SIDE} y {MAX_CANVAS_SIDE} px
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600 text-center">
            {error}
          </p>
        )}

        <div className="flex items-center justify-center gap-4">
          <Button
            label="Cancelar"
            variant="secondary"
            className="cursor-pointer rounded-full min-w-40"
            disabled={isSubmitting}
            onClick={handleClose}
          />
          <Button
            label={isSubmitting ? 'Creando...' : 'Crear'}
            variant="primary"
            className="cursor-pointer rounded-full min-w-40"
            disabled={!canCreate || isSubmitting}
            onClick={handleCreate}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CreateCanvasModal;
